import { googleLogin } from "@/services/auth-service";
import {
    AuthDTO,
    LoginRequestDTO,
    LoginUserDTO,
    SocialLoginRequestDTO,
    UserRole,
} from "@/types/api/auth";
import { User } from "@/types/user";
import { create } from "zustand";


const normalizeStatus = (status?: unknown): User['status'] => {
    if (typeof status !== 'string') return undefined;
    const lower = status.toLowerCase();
    return lower === 'verified' || lower === 'pending' || lower === 'rejected' ? lower : undefined;
};


interface AuthStoreState {
    user: User | null;
    loading: boolean;
    auth: AuthDTO | null;
    login: (data: LoginRequestDTO) => Promise<void>;
    loginViaSocials: (data: SocialLoginRequestDTO) => Promise<void>;
    setAuthState: (auth: AuthDTO) => void;
    logout: () => Promise<void>;
    canDoAction: (role: UserRole) => boolean;
}

type InitialState = {
    user: User | null;
    token: string | null;
    auth: AuthDTO | null;
    isAuthenticated: boolean;
    userRoles: UserRole | '';
};

const emptyInitialState: InitialState = {
    user: null,
    token: null,
    auth: null,
    isAuthenticated: false,
    userRoles: ''
};

const coerceAuthDTO = (value: unknown): AuthDTO | null => {
    if (!value || typeof value !== 'object') return null;
    const candidate = value as Partial<AuthDTO> & { auth?: AuthDTO };

    // Support legacy shape { auth: {...} }
    if (candidate.auth) {
        return coerceAuthDTO(candidate.auth);
    }

    const user = candidate.user as (LoginUserDTO & { status?: unknown }) | undefined;
    const token = candidate.token as string | undefined;
    const role = (candidate.role ?? user?.role) as UserRole | undefined;

    if (!user || !token) return null;

    const resolvedRole = (role ?? 'student') as UserRole;
    const normalizedUser: LoginUserDTO = {
        ...user,
        role: user.role ?? resolvedRole,
        status: normalizeStatus(user.status)
    };

    return {
        user: normalizedUser,
        token,
        role: resolvedRole
    };
};

const getInitialState = (): InitialState => {
    if (typeof window === 'undefined') {
        return emptyInitialState;
    }

    try {
        const savedAuth = localStorage.getItem('auth');
        if (savedAuth) {
            const parsed = JSON.parse(savedAuth);
            const auth = coerceAuthDTO(parsed);
            if (auth) {
                console.log('Loading saved auth for user:', auth.user.email);
                const userForState: User = {
                    id: auth.user.id,
                    email: auth.user.email,
                    name: auth.user.name,
                    role: auth.user.role,
                    track: auth.user.track,
                    status: normalizeStatus(auth.user.status),
                };
                return {
                    user: userForState,
                    token: auth.token,
                    auth,
                    isAuthenticated: true,
                    userRoles: auth.role
                };
            }
        }
    } catch (error) {
        console.error("Error parsing saved authentication", error);
        localStorage.removeItem('auth');
    }

    return emptyInitialState;
};

const initialState = getInitialState();


export const useAuthStore = create<AuthStoreState>((set, get) => {
    const persistAuth = (authPayload: AuthDTO) => {
        const normalizedUser: LoginUserDTO = {
            ...authPayload.user,
            status: normalizeStatus(authPayload.user.status),
        };
        const userForState: User = {
            id: normalizedUser.id,
            email: normalizedUser.email,
            name: normalizedUser.name,
            role: normalizedUser.role,
            track: normalizedUser.track,
            status: normalizeStatus(normalizedUser.status),
        };
        const normalizedPayload: AuthDTO = { ...authPayload, user: normalizedUser };
        set({
            user: userForState,
            auth: normalizedPayload,
        });

        if (typeof window !== 'undefined') {
            localStorage.setItem('auth', JSON.stringify(normalizedPayload));
        }
    };

    return {
        user: initialState.user,
        loading: false,
        auth: initialState.auth,
        login: async () => {

        },
        logout: async () => {
            // Clear auth state locally (server-side revocation can be added here).
            set({ user: null, auth: null });
            try {
                if (typeof window !== 'undefined') {
                    localStorage.removeItem('auth');
                }
            } catch {}
        },
        canDoAction: (role: UserRole) => {
            const { user } = get();
            if (!user || !user.role) return false;

            return user.role === role;
        },
        setAuthState: persistAuth,
        loginViaSocials: async (data: SocialLoginRequestDTO) => {
            const response = await googleLogin(data);
            const resolvedRole = (data.role ?? response.user.role ?? 'student') as UserRole;

            const userWithRole: LoginUserDTO = {
                ...response.user,
                role: resolvedRole,
            };

            const authPayload: AuthDTO = {
                user: userWithRole,
                token: response.token,
                role: resolvedRole,
            };

            persistAuth(authPayload);
        }
    };
})
